/**
 * Turn whatever a person drops on `/import` into uploadable history files.
 *
 * Client-side unzip is the parent plan's own design (§14): the zip Spotify
 * sends contains PDFs and video history nobody asked to upload, so the
 * browser opens it, keeps only the streaming-history JSON, and the server
 * never stores a byte it will not parse. The light "Account data" files are
 * deliberately *kept* here — the server recognizes them at finalize and
 * answers with the message explaining what to request instead, which beats a
 * silent client-side shrug.
 */

import { readZip } from './zip.js'

// Extended history ("Streaming_History_Audio_2017-2020_0.json", older
// "endsong_3.json") and the light export ("StreamingHistory0.json",
// "StreamingHistory_music_2.json"). Video history is excluded by name.
const HISTORY_FILE = /(^|\/)(streaming_?history(?!_video)[^/]*|endsong[^/]*)\.json$/i

// 1 MB, not 4. At 4 MB a typical ~12 MB `endsong_N.json` is exactly three
// chunks, so the bar reported 33 · 66 · 100 and nothing in between — which is
// what a person reads as "stuck, then a jump". A quarter of the size is four
// times the requests, and that is the whole trade: each is a `fetch` at the
// same origin against an idempotent byte-range write, the endpoint is not rate
// limited, and the server's own cap is `import_chunk_max_bytes` at 4 MB
// (`api/app/core/settings.py`) with Caddy's outer bound at 80 MB, so this is
// well inside both. If a real multi-file export ever regresses on it, 2 MB.
export const CHUNK_BYTES = 1024 * 1024

/**
 * `files` are DOM `File`s (a drop or a picker). Returns
 * `{ found: [{ name, bytes }], ignored: [names], empty: [names] }`, zips
 * opened in place. `empty` is a history file with no bytes, which the server
 * would refuse — kept apart so the page can say why rather than send it.
 */
export async function collectHistoryFiles(files) {
  const found = []
  const ignored = []
  const empty = []
  for (const file of files) {
    if (/\.zip$/i.test(file.name)) {
      const entries = readZip(await file.arrayBuffer())
      let any = false
      for (const entry of entries) {
        if (HISTORY_FILE.test(entry.name) && entry.size > 0) {
          found.push({ name: entry.name.split('/').pop(), bytes: await entry.read() })
          any = true
        }
      }
      if (!any) ignored.push(`${file.name} (no streaming history inside)`)
    } else if (HISTORY_FILE.test(file.name) && file.size === 0) {
      empty.push(file.name)
    } else if (HISTORY_FILE.test(file.name)) {
      found.push({ name: file.name, bytes: new Uint8Array(await file.arrayBuffer()) })
    } else {
      ignored.push(file.name)
    }
  }
  found.sort((a, b) => a.name.localeCompare(b.name))
  return { found, ignored, empty }
}

export async function sha256Hex(bytes) {
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('')
}
